class SiteHeader extends HTMLElement {
  static SCROLL_THRESHOLD = 5;
  static TRANSITION_DURATION = 300;

  connectedCallback() {
    this.lastScrollY = window.scrollY;
    this.ticking = false;
    this.solidTimeout = null;
    this.handleScroll = this.handleScroll.bind(this);
    this.handleCartUpdated = this.onCartUpdated.bind(this);

    window.addEventListener("scroll", this.handleScroll, { passive: true });
    document.addEventListener("cart:updated", this.handleCartUpdated);

    this.initDropdowns();
  }

  disconnectedCallback() {
    window.removeEventListener("scroll", this.handleScroll);
    document.removeEventListener("cart:updated", this.handleCartUpdated);
    clearTimeout(this.solidTimeout);
    this.cleanupDropdowns();
  }

  // -- Dropdowns --

  initDropdowns() {
    this.dropdownCleanup = [];
    const triggers = this.querySelectorAll("[data-dropdown-trigger]");

    for (const trigger of triggers) {
      const id = trigger.dataset.dropdownTrigger;
      const panel = this.querySelector(`[data-dropdown-panel="${id}"]`);
      if (!panel) continue;

      let closeTimeout = null;

      const open = () => {
        clearTimeout(closeTimeout);
        for (const p of this.querySelectorAll("[data-dropdown-panel]")) {
          if (p !== panel) this.closePanel(p);
        }
        this.openPanel(panel, trigger);
        trigger.setAttribute("aria-expanded", "true");
        this.setAttribute("data-solid", "");
      };

      const scheduleClose = () => {
        closeTimeout = setTimeout(() => {
          this.closePanel(panel);
          trigger.setAttribute("aria-expanded", "false");
          if (
            !this.querySelector('[data-dropdown-panel][aria-hidden="false"]')
          ) {
            if (
              window.scrollY <= 0 &&
              !this.hasAttribute("data-solid-default")
            ) {
              this.removeAttribute("data-solid");
            }
          }
        }, 150);
      };

      const onKeydown = (e) => {
        if (e.key === "Escape") {
          this.closePanel(panel);
          trigger.setAttribute("aria-expanded", "false");
          trigger.focus();
        }
      };

      trigger.addEventListener("mouseenter", open);
      trigger.addEventListener("mouseleave", scheduleClose);
      trigger.addEventListener("focusin", open);
      trigger.addEventListener("focusout", scheduleClose);
      trigger.addEventListener("keydown", onKeydown);

      panel.addEventListener("mouseenter", open);
      panel.addEventListener("mouseleave", scheduleClose);
      panel.addEventListener("focusin", open);
      panel.addEventListener("focusout", scheduleClose);
      panel.addEventListener("keydown", onKeydown);

      this.dropdownCleanup.push(() => {
        clearTimeout(closeTimeout);
        trigger.removeEventListener("mouseenter", open);
        trigger.removeEventListener("mouseleave", scheduleClose);
        trigger.removeEventListener("focusin", open);
        trigger.removeEventListener("focusout", scheduleClose);
        trigger.removeEventListener("keydown", onKeydown);
        panel.removeEventListener("mouseenter", open);
        panel.removeEventListener("mouseleave", scheduleClose);
        panel.removeEventListener("focusin", open);
        panel.removeEventListener("focusout", scheduleClose);
        panel.removeEventListener("keydown", onKeydown);
      });
    }
  }

  openPanel(panel, trigger) {
    const isFade = panel.hasAttribute("data-dropdown-fade");
    if (isFade && trigger) {
      const headerRect = this.getBoundingClientRect();
      const triggerRect = trigger.getBoundingClientRect();
      const triggerCenter =
        triggerRect.left + triggerRect.width / 2 - headerRect.left;
      panel.style.left = triggerCenter + "px";
      panel.style.transform = "translateX(-50%)";
      panel.classList.remove("opacity-0", "invisible", "pointer-events-none");
    } else {
      panel.style.gridTemplateRows = "1fr";
    }
    panel.setAttribute("aria-hidden", "false");
  }

  closePanel(panel) {
    const isFade = panel.hasAttribute("data-dropdown-fade");
    if (isFade) {
      panel.classList.add("opacity-0", "invisible", "pointer-events-none");
    } else {
      panel.style.gridTemplateRows = "0fr";
    }
    panel.setAttribute("aria-hidden", "true");
  }

  cleanupDropdowns() {
    for (const cleanup of this.dropdownCleanup || []) {
      cleanup();
    }
    this.dropdownCleanup = [];
  }

  // -- Cart --

  async onCartUpdated() {
    try {
      const response = await fetch(
        window.Shopify.routes.root + "?sections=header",
      );
      const data = await response.json();
      const html = new DOMParser().parseFromString(data["header"], "text/html");
      const newToggle = html.querySelector("[data-cart-toggle]");
      const currentToggle = this.querySelector("[data-cart-toggle]");
      if (newToggle && currentToggle) {
        currentToggle.innerHTML = newToggle.innerHTML;
      }
    } catch (error) {
      console.error("Header cart count update error:", error);
    }
  }

  // -- Scroll --

  handleScroll() {
    if (this.ticking) return;
    this.ticking = true;

    requestAnimationFrame(() => {
      const scrollY = window.scrollY;
      const delta = scrollY - this.lastScrollY;

      if (Math.abs(delta) >= SiteHeader.SCROLL_THRESHOLD) {
        const isScrollingDown = delta > 0;
        this.toggleAttribute("data-hidden", isScrollingDown);

        if (
          isScrollingDown &&
          !this.hasAttribute("data-solid") &&
          scrollY > 0
        ) {
          clearTimeout(this.solidTimeout);
          this.solidTimeout = setTimeout(() => {
            this.toggleAttribute("data-solid", true);
          }, SiteHeader.TRANSITION_DURATION);
        }

        this.lastScrollY = scrollY;
      }

      if (scrollY <= 0) {
        this.removeAttribute("data-hidden");
        clearTimeout(this.solidTimeout);
        if (!this.hasAttribute("data-solid-default")) {
          const hasOpenPanel = this.querySelector(
            '[data-dropdown-panel][aria-hidden="false"]',
          );
          if (!hasOpenPanel) {
            this.removeAttribute("data-solid");
          }
        }
      }

      this.ticking = false;
    });
  }
}

if (!customElements.get("site-header")) {
  customElements.define("site-header", SiteHeader);
}
